import React, { ComponentType } from "react";
import _, { isEqual } from "lodash";
import { Event } from "./Emitter"
import { AbstractGetAllStore, AbstractStore, QueryStore } from "./AbstractStores";

export type ComponentConstructor<T = {}> = React.ComponentClass<T> | React.StatelessComponent<T>;

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
export type NonNullablePropertiesNamesOfType<TItem, T> = { [P in keyof TItem]-?: TItem[P] extends T ? P : never }[keyof TItem];
export type PropertiesNamesOfType<TItem, T> = { [P in keyof TItem]-?: NonNullable<TItem[P]> extends T ? P : never }[keyof TItem];
export type StringPropertiesNamesOfType<TItem, T> = Extract<PropertiesOfType<TItem, T>, string>;
export type PropertiesOfType<TItem, T> = Pick<TItem, PropertiesNamesOfType<TItem, T>>;

export function requiresBase<TProps, TPicked extends keyof TProps>(
    WrappedComponent: ComponentType<TProps>,
    provider: {
        key: TPicked,
        store: { Changed: Event },
        getFromStore: () => TProps[TPicked],
        getAll: () => Promise<{}>,
        needsAction: () => boolean;
    },
    loading?: ComponentConstructor<{}>,
) {
    return class InnerCmp extends React.Component<Omit<TProps, TPicked>, { data: TProps[TPicked] }> {
        constructor(props: Omit<TProps, TPicked>) {
            super(props);
            this.state = {
                data: provider.getFromStore(),
            }
        }

        listener = () => this.setState({ data: provider.getFromStore() });

        componentDidMount() {
            provider.store.Changed.register(this.listener)
            if (provider.needsAction())
                provider.getAll();
        }

        componentWillUnmount() {
            provider.store.Changed.unregister(this.listener)
        }

        render() {
            const Loading = loading;

            if (provider.needsAction()) {
                if (!Loading)
                    return null;
                else
                    return <Loading />
            }

            var WC = WrappedComponent as any;
            const props = { ...this.props } as any as TProps;
            props[provider.key] = this.state.data;
            return (<WC {...props} />);
        }
    }
}

export function getAllRequired<TProps>(
    WrappedComponent: ComponentType<TProps>,
    store: AbstractGetAllStore<any> | QueryStore<any>,
    getAll: () => void,
    loading?: ComponentConstructor<{}>) {
    return class InnerCmp extends React.Component<TProps> {
        listener = () => {
            this.forceUpdate();
        }

        isInitialized() {
            if (store instanceof QueryStore) {
                if (!store.hasQueryAll()) return false;
            }
            else if (!store.isInitialized()) {
                return false
            }
            return true;
        }
        componentDidMount() {
            store.Changed.register(this.listener)
            if (!this.isInitialized())
                getAll();

        }
        componentWillUnmount() {
            store.Changed.unregister(this.listener)
        }

        render() {
            const Loading = loading;
            if (!this.isInitialized())
                return Loading ? <Loading /> : null;
            var WC = WrappedComponent;
            return (<WC {...this.props} />);
        }
    }
}



export let With = <TProps extends {}, T, TStore extends AbstractStore<T> & { isInitialized: () => boolean }>
    (cmp: ComponentConstructor<TProps>, s: TStore, action: () => Promise<T[]>, loading?: ComponentConstructor<{}>) =>
    <TPicked extends keyof TProps>
        (getAll: (store: TStore) => TProps[TPicked], key: TPicked) => requiresBase(cmp, {
            getAll: () => action(),
            key,
            getFromStore: () => getAll(s),
            needsAction: () => !s.isInitialized(),
            store: s
        }, loading);

export let Bind = <TProps extends {}, TResult>
    (cmp: ComponentConstructor<TProps>, s: () => TResult | undefined, change: Event, action: () => Promise<{}>, loading?: ComponentConstructor<{}>) =>
    <TPicked extends keyof PropertiesOfType<TProps, TResult>>
        (key: TPicked) => requiresBase(cmp, {
            getAll: () => action(),
            key,
            getFromStore: () => s()! as any,
            needsAction: () => s() === undefined,
            store: { Changed: change }
        }, loading);


export function attach<TComponent extends React.Component<TProps>, TProps, TRemovedProps extends keyof Partial<TProps>, TStore extends { Changed: Event }>(
    WrappedComponent: ComponentType<TProps>,
    store: TStore,
    mapStateToProps: (state: TStore, props: TProps) => { [P in TRemovedProps]: TProps[P] } | null) {
    const Cmp = class InnerCmp extends React.Component<Omit<TProps, TRemovedProps> & { forwardedRef: React.Ref<TComponent> }, { data: { [P in TRemovedProps]: TProps[P] } | null }> {

        constructor(props: Omit<TProps, TRemovedProps> & { forwardedRef: React.Ref<TComponent> }) {
            super(props);
            this.state = InnerCmp.getDerivedStateFromProps(props as any);
            store.Changed.register(this.listener);
        }

        static getDerivedStateFromProps(props: TProps) {
            return { data: mapStateToProps(store, props) };
        }

        listener = () => {
            const data = mapStateToProps(store, this.props as any);
            if (!isEqual(this.state.data, data)) {
                this.setState({ data });
            }
        }

        componentWillUnmount() {
            store.Changed.unregister(this.listener)
        }

        render() {
            if (this.state.data == null)
                return null;
            var WC = WrappedComponent as any;
            return (<WC ref={this.props.forwardedRef} {...this.props} {...this.state.data} />);
        }
    }
    return React.forwardRef<TComponent, Omit<TProps, TRemovedProps>>((props, ref) => <Cmp forwardedRef={ref} {...props} />);
}

